/**
 * Error utilities — single source of truth for error handling and message formatting.
 */

// ─── Types ────────────────────────────────────────────────────────────────────

export interface ErrorWithStatus {
  status?: number
  response?: { status?: number; data?: any }
  error?: { message?: string }
  message?: string
  type?: string
  name?: string
  code?: string
}

export interface ErrorData {
  data: any
  errors: string[]
  message: string
  errorString: string
}

// ─── Status Code Messages ─────────────────────────────────────────────────────

export function getStatusErrorMessage(status: number, defaultMessage?: string): string {
  switch (status) {
    case 400:
      return "Something doesn't look right — double-check your info and try again."
    case 401:
      return "You'll need to sign in to continue."
    case 403:
      return "You don't have permission to do that."
    case 404:
      return "We couldn't find what you're looking for."
    case 408:
      return 'That took too long. Give it another try.'
    case 409:
      return 'Something got out of sync. Refresh the page and try again.'
    case 422:
      return 'Some of the info you entered looks off. Take another look.'
    case 429:
      return 'Too many attempts. Wait a moment and try again.'
    case 500:
      return 'Something went wrong on our end. Try again in a moment.'
    case 502:
      return 'Our servers are having a moment. Try again shortly.'
    case 503:
      return "We're temporarily down. Try again in a bit."
    case 504:
      return 'The server took too long to respond. Try again?'
    default:
      return defaultMessage || 'Something unexpected happened. Try again?'
  }
}

// ─── Error Data Extraction ────────────────────────────────────────────────────

export function getErrorData(error: any): ErrorData {
  let errorData = error.response

  if (typeof errorData === 'object' && 'data' in errorData) {
    errorData = errorData.data
    if (typeof errorData === 'object' && 'data' in errorData) {
      errorData = errorData.data
    }
  }

  let errors: string[] = []
  let msg = ''

  if (typeof errorData === 'string') {
    msg = errorData
    errorData = null
  }

  if (errorData) {
    Object.keys(errorData).forEach(key => {
      if (key === 'error_message') {
        msg = errorData[key]
      } else {
        errors = errors.concat(errorData[key])
      }
    })
  } else {
    msg = error.error ? error.error.message : error.message
    if (msg) {
      errors = [msg]
    }
  }

  return {
    data: errorData,
    errors,
    message: msg,
    errorString: errorData ? JSON.stringify(errorData) : msg,
  }
}

// ─── Main Formatter ───────────────────────────────────────────────────────────

export function formatErrorMessage(error: any): string {
  if (!error) {
    return 'Something went wrong. Try again?'
  }
  if (typeof error === 'string') {
    return error
  }

  // Network errors (check type flag and message, case-insensitive)
  if (
    error.type === 'network' ||
    error.name === 'NetworkError' ||
    error.message?.toLowerCase().includes('network error')
  ) {
    return "Can't connect — check your internet and try again."
  }

  // Timeout errors
  if (error.code === 'ECONNABORTED' || error.message?.toLowerCase().includes('timeout')) {
    return 'That took too long. Give it another try.'
  }

  // HTTP status — check FIRST so raw axios messages never leak through
  const status = error.status || error.response?.status
  if (status) {
    const apiMessage = error.response?.data?.message || error.response?.data?.error_message
    if (apiMessage && typeof apiMessage === 'string') {
      return apiMessage
    }
    return getStatusErrorMessage(status)
  }

  // No status — extract from error data
  const errorData = getErrorData(error)
  if (errorData.message) {
    return errorData.message
  }
  if (errorData.errors.length > 0) {
    return errorData.errors.join(', ')
  }

  return 'Something unexpected happened. Try again?'
}

// ─── Simple Helpers ───────────────────────────────────────────────────────────

/** Simple message extraction — use formatErrorMessage for user-facing messages. */
export function getErrorMessage(error: ErrorWithStatus): string {
  const status = error?.status || error?.response?.status || 500
  if (status === 404) {
    return error.error?.message || error.message || 'Not found'
  }
  if (status >= 500) {
    return 'Something went wrong on our end. Try again in a moment.'
  }
  return error.error?.message || error.message || 'An error occurred'
}

export function isServerError(error: ErrorWithStatus): boolean {
  const status = error.status || error.response?.status
  return status ? status >= 500 : false
}

export function isAuthError(error: ErrorWithStatus): boolean {
  const status = error.status || error.response?.status
  return status === 401 || status === 403
}

export function isNotFoundError(error: ErrorWithStatus): boolean {
  const status = error.status || error.response?.status
  return status === 404
}

export function isCriticalError(error: any): boolean {
  const status = error.status || error.response?.status
  const criticalStatuses = [500, 502, 503, 504, 401, 403]
  return (
    !status ||
    criticalStatuses.includes(status) ||
    error.name === 'NetworkError' ||
    error.message?.toLowerCase().includes('network error')
  )
}
